import React, { useEffect, useState, useCallback } from 'react';
import { contractAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TEMPLATE_TYPES = [
  { value: 'SPONSORED_POST',   label: 'Sponsored Content',    icon: '📢', desc: 'One-time sponsored post, video or reel' },
  { value: 'AFFILIATE',        label: 'Affiliate Marketing',  icon: '🔗', desc: 'Commission-based affiliate partnership' },
  { value: 'UGC',              label: 'UGC Agreement',        icon: '🎬', desc: 'User-generated content for brand to use' },
  { value: 'BRAND_AMBASSADOR', label: 'Brand Ambassador',     icon: '🌟', desc: 'Long-term brand representation' },
  { value: 'CUSTOM',           label: 'Custom Agreement',     icon: '📝', desc: 'Write your own custom terms' },
];

const STATUS_CONFIG = {
  DRAFT:     { bg: '#f5f5f5', color: '#666',    label: 'Draft'     },
  SENT:      { bg: '#e8f4fd', color: '#1565c0', label: 'Sent'      },
  SIGNED:    { bg: '#e8f5e9', color: '#2e7d32', label: 'Signed'    },
  ACTIVE:    { bg: '#e8f5e9', color: '#1a7a4a', label: 'Active'    },
  COMPLETED: { bg: '#f3e5f5', color: '#6a1b9a', label: 'Completed' },
  CANCELLED: { bg: '#fce4ec', color: '#c62828', label: 'Cancelled' },
};

const emptyForm = {
  templateType: 'SPONSORED_POST', projectTitle: '', platform: '',
  startDate: '', endDate: '', status: 'DRAFT',
  creatorName: '', creatorAddress: '', creatorGstin: '', creatorEmail: '', creatorPhone: '',
  brandName: '', brandContact: '', brandEmail: '', brandAddress: '', brandGstin: '',
  deliverables: '', paymentAmount: '', paymentTerms: '50% advance, 50% on content delivery',
  paymentDueDate: '', revisions: '1 round of revisions included within 3 working days',
  exclusivity: '', contentRights: '', confidentiality: true,
  governingLaw: 'Maharashtra, India', additionalTerms: '', notes: '',
};

const TABS = ['Template', 'Parties', 'Deliverables', 'Payment', 'Legal'];

export default function ContractsPage() {
  const { user } = useAuth();
  const [contracts, setContracts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(emptyForm);
  const [tab, setTab]               = useState(0);
  const [saving, setSaving]         = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError]           = useState('');

  const load = useCallback(async () => {
    try { const r = await contractAPI.getAll(); setContracts(r.data); }
    catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (updates) => setForm(p => ({ ...p, ...updates }));

  const openNew = (templateType = 'SPONSORED_POST') => {
    setEditId(null);
    setTab(0);
    setError('');
    setForm({
      ...emptyForm,
      templateType,
      creatorName:    user?.businessName || user?.name    || '',
      creatorEmail:   user?.email        || '',
      creatorGstin:   user?.gstin        || '',
      creatorAddress: user?.address      || '',
      creatorPhone:   user?.phone        || '',
    });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setTab(0);
    setError('');
    setForm({
      templateType:    c.templateType    || 'SPONSORED_POST',
      projectTitle:    c.projectTitle    || '',
      platform:        c.platform        || '',
      startDate:       c.startDate       || '',
      endDate:         c.endDate         || '',
      status:          c.status          || 'DRAFT',
      creatorName:     c.creatorName     || '',
      creatorAddress:  c.creatorAddress  || '',
      creatorGstin:    c.creatorGstin    || '',
      creatorEmail:    c.creatorEmail    || '',
      creatorPhone:    c.creatorPhone    || '',
      brandName:       c.brandName       || '',
      brandContact:    c.brandContact    || '',
      brandEmail:      c.brandEmail      || '',
      brandAddress:    c.brandAddress    || '',
      brandGstin:      c.brandGstin      || '',
      deliverables:    c.deliverables    || '',
      paymentAmount:   c.paymentAmount   || '',
      paymentTerms:    c.paymentTerms    || '',
      paymentDueDate:  c.paymentDueDate  || '',
      revisions:       c.revisions       || '',
      exclusivity:     c.exclusivity     || '',
      contentRights:   c.contentRights   || '',
      confidentiality: c.confidentiality !== false,
      governingLaw:    c.governingLaw    || 'Maharashtra, India',
      additionalTerms: c.additionalTerms || '',
      notes:           c.notes           || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.projectTitle.trim()) { setError('Project title required'); return; }
    if (!form.brandName.trim())    { setError('Brand name required');    return; }
    setSaving(true); setError('');
    try {
      if (editId) await contractAPI.update(editId, form);
      else        await contractAPI.create(form);
      setShowForm(false);
      load();
    } catch (e) { setError(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    await contractAPI.delete(id);
    load();
  };

  const handleStatusChange = async (id, status) => {
    await contractAPI.updateStatus(id, status);
    load();
  };

  const handleDownload = async (c) => {
    setDownloading(c.id);
    try {
      const res = await contractAPI.downloadPdf(c.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${c.contractNumber}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to download'); }
    setDownloading(null);
  };

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading…</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1a1a2e' }}>Contract Templates 📄</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.875rem' }}>Professional agreements for brand deals — generate PDF instantly</p>
        </div>
        <button onClick={() => openNew()} style={primaryBtn}>+ New Contract</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total',     val: contracts.length,                                           icon: '📄' },
          { label: 'Draft',     val: contracts.filter(c => c.status === 'DRAFT').length,         icon: '✏️' },
          { label: 'Signed',    val: contracts.filter(c => c.status === 'SIGNED').length,        icon: '✅' },
          { label: 'Active',    val: contracts.filter(c => c.status === 'ACTIVE').length,        icon: '🟢' },
          { label: 'Completed', val: contracts.filter(c => c.status === 'COMPLETED').length,     icon: '🏁' },
        ].map(s => (
          <div key={s.label} style={statCard}>
            <div style={{ fontSize: '1.2rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a2e' }}>{s.val}</div>
            <div style={{ fontSize: '0.72rem', color: '#888' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Template Picker */}
      {contracts.length === 0 && (
        <div style={{ background: 'white', borderRadius: 16, padding: '1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: 800, color: '#1a1a2e', marginBottom: '1rem' }}>Choose a contract template to get started:</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {TEMPLATE_TYPES.map(t => (
              <button key={t.value} onClick={() => openNew(t.value)} style={templateCard}>
                <div style={{ fontSize: '1.5rem' }}>{t.icon}</div>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>{t.label}</div>
                <div style={{ fontSize: '0.72rem', color: '#888' }}>{t.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Contract List */}
      {contracts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {contracts.map(c => {
            const tmpl = TEMPLATE_TYPES.find(t => t.value === c.templateType);
            const sc   = STATUS_CONFIG[c.status] || STATUS_CONFIG.DRAFT;
            return (
              <div key={c.id} style={contractCard}>
                <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>{tmpl?.icon || '📄'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 800, color: '#1a1a2e' }}>{c.projectTitle}</span>
                    <span style={{ ...badge, background: sc.bg, color: sc.color }}>{sc.label}</span>
                    <span style={{ fontSize: '0.72rem', color: '#aaa' }}>{c.contractNumber}</span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {tmpl?.label} · <b style={{ color: '#1a1a2e' }}>{c.brandName}</b>
                    {c.paymentAmount && <span> · ₹{Number(c.paymentAmount).toLocaleString('en-IN')}</span>}
                    {c.startDate && <span> · {c.startDate}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                  <select value={c.status} onChange={e => handleStatusChange(c.id, e.target.value)} style={selectSmall}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  <button onClick={() => handleDownload(c)} style={iconBtn} disabled={downloading === c.id} title="Download PDF">
                    {downloading === c.id ? '⏳' : '⬇️'}
                  </button>
                  <button onClick={() => openEdit(c)} style={iconBtn} title="Edit">✏️</button>
                  <button onClick={() => handleDelete(c.id)} style={{ ...iconBtn, color: '#e53e3e' }} title="Delete">🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────── */}
      {showForm && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e' }}>
                {editId ? 'Edit Contract' : 'New Contract'}
              </h2>
              <button onClick={() => setShowForm(false)} style={closeBtn}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '1.25rem', background: '#f5f5f5', padding: '0.25rem', borderRadius: 10 }}>
              {TABS.map((t, i) => (
                <button key={t} onClick={() => setTab(i)} style={{
                  flex: 1, padding: '0.45rem', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.75rem',
                  background: tab === i ? 'white' : 'transparent',
                  color: tab === i ? '#1a7a4a' : '#888',
                  boxShadow: tab === i ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}>{t}</button>
              ))}
            </div>

            {/* Tab 0: Template */}
            {tab === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.6rem' }}>
                  {TEMPLATE_TYPES.map(t => (
                    <button key={t.value} onClick={() => f({ templateType: t.value })} style={{
                      padding: '0.75rem', border: '2px solid', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                      borderColor: form.templateType === t.value ? '#1a7a4a' : '#e0e0e0',
                      background: form.templateType === t.value ? '#f0f9f4' : 'white',
                    }}>
                      <div style={{ fontSize: '1.3rem' }}>{t.icon}</div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: form.templateType === t.value ? '#1a7a4a' : '#444' }}>{t.label}</div>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginTop: '0.5rem' }}>
                  <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Project / Campaign Title *
                    <input value={form.projectTitle} onChange={e => f({ projectTitle: e.target.value })} style={input} placeholder="e.g. Diwali Campaign — YouTube Integration" />
                  </label>
                  <label style={labelStyle}>Platform(s)
                    <input value={form.platform} onChange={e => f({ platform: e.target.value })} style={input} placeholder="YouTube, Instagram, Both" />
                  </label>
                  <label style={labelStyle}>Status
                    <select value={form.status} onChange={e => f({ status: e.target.value })} style={input}>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>Start Date
                    <input value={form.startDate} onChange={e => f({ startDate: e.target.value })} style={input} type="date" />
                  </label>
                  <label style={labelStyle}>End Date
                    <input value={form.endDate} onChange={e => f({ endDate: e.target.value })} style={input} type="date" />
                  </label>
                </div>
              </div>
            )}

            {/* Tab 1: Parties */}
            {tab === 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div style={sectionLabel}>📤 Creator (You)</div>
                <label style={labelStyle}>Name / Business Name
                  <input value={form.creatorName} onChange={e => f({ creatorName: e.target.value })} style={input} />
                </label>
                <label style={labelStyle}>Email
                  <input value={form.creatorEmail} onChange={e => f({ creatorEmail: e.target.value })} style={input} type="email" />
                </label>
                <label style={labelStyle}>GSTIN
                  <input value={form.creatorGstin} onChange={e => f({ creatorGstin: e.target.value.toUpperCase() })} style={input} placeholder="27AAAAA0000A1Z5" />
                </label>
                <label style={labelStyle}>Phone
                  <input value={form.creatorPhone} onChange={e => f({ creatorPhone: e.target.value })} style={input} />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Address
                  <textarea value={form.creatorAddress} onChange={e => f({ creatorAddress: e.target.value })} style={{ ...input, height: 55, resize: 'vertical' }} />
                </label>

                <div style={sectionLabel}>🏢 Brand / Client</div>
                <label style={labelStyle}>Brand Name *
                  <input value={form.brandName} onChange={e => f({ brandName: e.target.value })} style={input} placeholder="e.g. Noise Gadgets Pvt Ltd" />
                </label>
                <label style={labelStyle}>Contact Person
                  <input value={form.brandContact} onChange={e => f({ brandContact: e.target.value })} style={input} placeholder="Marketing Manager name" />
                </label>
                <label style={labelStyle}>Brand Email
                  <input value={form.brandEmail} onChange={e => f({ brandEmail: e.target.value })} style={input} type="email" />
                </label>
                <label style={labelStyle}>Brand GSTIN
                  <input value={form.brandGstin} onChange={e => f({ brandGstin: e.target.value.toUpperCase() })} style={input} />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Brand Address
                  <textarea value={form.brandAddress} onChange={e => f({ brandAddress: e.target.value })} style={{ ...input, height: 55, resize: 'vertical' }} />
                </label>
              </div>
            )}

            {/* Tab 2: Deliverables */}
            {tab === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <label style={labelStyle}>Deliverables (what you will create)
                  <textarea value={form.deliverables} onChange={e => f({ deliverables: e.target.value })}
                    style={{ ...input, height: 100, resize: 'vertical' }}
                    placeholder="e.g. 1 YouTube video (8-12 mins) with 60-90 sec brand integration in first half, 2 Instagram Reels (30 sec each), 3 Instagram Stories with swipe-up link. All content to go live between Oct 15-30, 2025." />
                </label>
                <label style={labelStyle}>Revisions Policy
                  <input value={form.revisions} onChange={e => f({ revisions: e.target.value })} style={input}
                    placeholder="e.g. 1 round of revisions included within 3 working days of draft submission" />
                </label>
                <div style={{ background: '#fff8e1', borderRadius: 10, padding: '0.75rem', fontSize: '0.8rem', color: '#7a5c00' }}>
                  💡 <b>Tip:</b> Be specific about deliverables — include format, duration, platform, and go-live dates. This protects you from scope creep.
                </div>
              </div>
            )}

            {/* Tab 3: Payment */}
            {tab === 3 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <label style={labelStyle}>Total Fee (₹)
                  <input value={form.paymentAmount} onChange={e => f({ paymentAmount: e.target.value })} style={input} type="number" placeholder="50000" />
                </label>
                <label style={labelStyle}>Payment Due Date
                  <input value={form.paymentDueDate} onChange={e => f({ paymentDueDate: e.target.value })} style={input} type="date" />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1/-1' }}>Payment Schedule
                  <input value={form.paymentTerms} onChange={e => f({ paymentTerms: e.target.value })} style={input}
                    placeholder="e.g. 50% advance before content creation, 50% within 7 days of content going live" />
                </label>
                <div style={{ gridColumn: '1/-1', background: '#e8f4fd', borderRadius: 10, padding: '0.75rem', fontSize: '0.8rem', color: '#1565c0' }}>
                  ℹ️ <b>Note:</b> The contract automatically includes a TDS clause — brand will provide Form 16A. Amount shown in contract is exclusive of GST (18%).
                </div>
              </div>
            )}

            {/* Tab 4: Legal */}
            {tab === 4 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <label style={labelStyle}>Exclusivity Clause
                  <textarea value={form.exclusivity} onChange={e => f({ exclusivity: e.target.value })}
                    style={{ ...input, height: 70, resize: 'vertical' }}
                    placeholder="e.g. Creator will not promote direct competing brands in the same product category for 30 days after content goes live." />
                </label>
                <label style={labelStyle}>Content Rights / Usage Rights
                  <textarea value={form.contentRights} onChange={e => f({ contentRights: e.target.value })}
                    style={{ ...input, height: 70, resize: 'vertical' }}
                    placeholder="Leave blank for default: Creator retains ownership, Brand may share on official channels only. Paid ads require separate consent." />
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                  <label style={labelStyle}>Governing Law (State, India)
                    <input value={form.governingLaw} onChange={e => f({ governingLaw: e.target.value })} style={input} placeholder="Maharashtra, India" />
                  </label>
                  <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '0.5rem', paddingTop: '1.4rem' }}>
                    <input type="checkbox" checked={form.confidentiality} onChange={e => f({ confidentiality: e.target.checked })} />
                    Include Confidentiality (NDA) clause
                  </label>
                </div>
                <label style={labelStyle}>Additional Terms (optional)
                  <textarea value={form.additionalTerms} onChange={e => f({ additionalTerms: e.target.value })}
                    style={{ ...input, height: 60, resize: 'vertical' }}
                    placeholder="Any other specific terms..." />
                </label>
                <div style={{ background: '#fce4ec', borderRadius: 10, padding: '0.75rem', fontSize: '0.78rem', color: '#c62828' }}>
                  ⚠️ <b>Disclaimer:</b> This is a template for reference. For legally binding contracts, consult a qualified legal professional in India.
                </div>
              </div>
            )}

            {error && <div style={{ color: '#e53e3e', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              {tab > 0 && <button onClick={() => setTab(t => t - 1)} style={outlineBtn}>← Back</button>}
              {tab < TABS.length - 1
                ? <button onClick={() => setTab(t => t + 1)} style={primaryBtn}>Next →</button>
                : <>
                    <button onClick={() => setShowForm(false)} style={outlineBtn}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={primaryBtn}>
                      {saving ? 'Saving…' : editId ? '💾 Update' : '💾 Create Contract'}
                    </button>
                  </>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const primaryBtn  = { background: '#1a7a4a', color: 'white', border: 'none', borderRadius: 10, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const outlineBtn  = { background: 'white', color: '#1a7a4a', border: '1.5px solid #1a7a4a', borderRadius: 10, padding: '0.6rem 1.1rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' };
const iconBtn     = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.55rem', cursor: 'pointer', fontSize: '0.9rem' };
const selectSmall = { padding: '0.35rem 0.5rem', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: '0.78rem', cursor: 'pointer', background: 'white' };
const badge       = { padding: '0.18rem 0.6rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 };
const input       = { padding: '0.55rem 0.75rem', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'white' };
const labelStyle  = { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: '#444' };
const sectionLabel = { gridColumn: '1/-1', fontWeight: 800, fontSize: '0.78rem', color: '#1a7a4a', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '2px solid #e8f5e9' };
const statCard    = { background: 'white', borderRadius: 14, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '0.15rem' };
const templateCard = { background: 'white', border: '1.5px solid #e0e0e0', borderRadius: 12, padding: '1rem', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.3rem', transition: 'all 0.15s' };
const contractCard = { background: 'white', borderRadius: 14, padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' };
const overlay     = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' };
const modal       = { background: 'white', borderRadius: 18, padding: '1.75rem', width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const closeBtn    = { background: '#f5f5f5', border: 'none', borderRadius: 8, padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '1rem' };
